import { useEffect, useRef, useCallback } from 'react';
import { PlayerEventType } from '../types';
export const KEYBOARD_SHORTCUTS = [
    { key: 'Space', description: 'Play / Pause' },
    { key: '←', description: 'Step back one frame' },
    { key: '→', description: 'Step forward one frame' },
    { key: 'Shift + ←', description: 'Step back one second' },
    { key: 'Shift + →', description: 'Step forward one second' },
    { key: '↑', description: 'Previous marker' },
    { key: '↓', description: 'Next marker' },
    { key: 'Home', description: 'Go to beginning' },
    { key: 'End', description: 'Go to end' },
    { key: 'Shift + T', description: 'Show Seek to Timecode' },
    { key: 'J', description: 'Rewind' },
    { key: 'K', description: 'Pause' },
    { key: 'L', description: 'Fast forward' },
    { key: 'K + J', description: 'Slow step backward (hold)' },
    { key: 'K + L', description: 'Slow step forward (hold)' },
    { key: ',', description: 'Back 5 seconds' },
    { key: '.', description: 'Forward 5 seconds' },
    { key: 'M', description: 'Mute / Unmute' },
    { key: 'Shift + F', description: 'Toggle fullscreen' },
    { key: 'C', description: 'Toggle captions' },
    { key: 'Escape', description: 'Close menus' },
];
export function useKeyboardNav({ mediaEl, containerEl, dispatch, fps, // eslint-disable-line @typescript-eslint/no-unused-vars
onSeek, // eslint-disable-line @typescript-eslint/no-unused-vars
onBeforeSeek, onFullscreenToggle, onMuteToggle, onNextMarker, onPrevMarker, onShowStatus, // eslint-disable-line @typescript-eslint/no-unused-vars
basePlaybackRateRef, subscribersRef, // eslint-disable-line @typescript-eslint/no-unused-vars
rewind, fastForward, startSlowStepBack, startSlowStepForward, stopSlowStep, // eslint-disable-line @typescript-eslint/no-unused-vars
clearRewind, clearSlowStep, incrementFrames, seekTo, isFollower = false, }) {
    const isKHeldRef = useRef(false);
    const slowStepActiveRef = useRef(false);
    const togglePlayback = useCallback(() => {
        if (!mediaEl)
            return;
        clearRewind();
        if (mediaEl.paused) {
            if (onBeforeSeek && !onBeforeSeek())
                return;
            mediaEl.playbackRate = basePlaybackRateRef?.current ?? 1.0;
            // Pre-notify subscribers so followers start playing BEFORE the
            // source — same pattern as handlePlayClick in HekatePlayer.
            // Without this, pressing spacebar to resume leaves a 50-100ms
            // gap before the follower hears the async 'play' event.
            for (const sub of subscribersRef.current) {
                sub({
                    type: PlayerEventType.PLAY,
                    currentTime: mediaEl.currentTime,
                    playbackRate: mediaEl.playbackRate,
                    isPaused: false,
                });
            }
            mediaEl.play();
        }
        else {
            mediaEl.pause();
        }
    }, [mediaEl, clearRewind, basePlaybackRateRef, onBeforeSeek, subscribersRef]);
    useEffect(() => {
        if (!containerEl || !mediaEl)
            return;
        const handleKeyDown = (e) => {
            // Don't capture keys when typing in input fields.
            // Use composedPath()[0] to see through Shadow DOM boundaries (e.g. <comment-textarea>).
            const target = e.target;
            const actualTarget = (e.composedPath()[0] ?? target);
            if (target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                actualTarget.tagName === 'INPUT' ||
                actualTarget.tagName === 'TEXTAREA' ||
                target.isContentEditable ||
                actualTarget.isContentEditable) {
                return;
            }
            // Playback keys that followers must not trigger locally. Non-playback
            // keys (M, Shift+F, C, Escape) continue through the switch below.
            const PLAYBACK_KEYS = new Set([
                ' ', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                'j', 'J', 'k', 'K', 'l', 'L',
                'Home', 'End', ',', '.',
            ]);
            if (isFollower && PLAYBACK_KEYS.has(e.key)) {
                return;
            }
            switch (e.key) {
                case ' ':
                    e.preventDefault();
                    togglePlayback();
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    if (onBeforeSeek && !onBeforeSeek())
                        break;
                    clearRewind();
                    if (e.shiftKey) {
                        seekTo(mediaEl.currentTime - 1.0);
                    }
                    else {
                        incrementFrames(-1);
                    }
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    if (onBeforeSeek && !onBeforeSeek())
                        break;
                    clearRewind();
                    if (e.shiftKey) {
                        seekTo(mediaEl.currentTime + 1.0);
                    }
                    else {
                        incrementFrames(1);
                    }
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    onPrevMarker?.();
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    onNextMarker?.();
                    break;
                case 'j':
                case 'J':
                    if (e.repeat)
                        break;
                    if (isKHeldRef.current) {
                        if (!slowStepActiveRef.current) {
                            slowStepActiveRef.current = true;
                            startSlowStepBack();
                        }
                    }
                    else {
                        rewind();
                    }
                    break;
                case 'k':
                case 'K':
                    // Use e.repeat to skip OS autorepeat keydowns (we only want to act
                    // on a fresh press). Don't gate on isKHeldRef — if a prior keyup
                    // was missed (e.g. focus shifted while paused), gating here would
                    // trap the player in a stuck "K held" state with no way to recover.
                    if (e.repeat)
                        break;
                    isKHeldRef.current = true;
                    clearRewind();
                    clearSlowStep();
                    slowStepActiveRef.current = false;
                    mediaEl.playbackRate = 1.0;
                    mediaEl.pause();
                    break;
                case 'l':
                case 'L':
                    if (e.repeat)
                        break;
                    if (isKHeldRef.current) {
                        if (!slowStepActiveRef.current) {
                            slowStepActiveRef.current = true;
                            startSlowStepForward();
                        }
                    }
                    else {
                        fastForward();
                    }
                    break;
                case 'Home':
                    e.preventDefault();
                    if (onBeforeSeek && !onBeforeSeek())
                        break;
                    clearRewind();
                    seekTo(0);
                    break;
                case 'End':
                    e.preventDefault();
                    if (onBeforeSeek && !onBeforeSeek())
                        break;
                    clearRewind();
                    seekTo(mediaEl.duration);
                    break;
                case 'f':
                case 'F':
                    if (e.shiftKey) {
                        onFullscreenToggle?.();
                    }
                    break;
                case ',':
                    e.preventDefault();
                    if (onBeforeSeek && !onBeforeSeek())
                        break;
                    clearRewind();
                    // seekTo handles clamping, onSeek callback, and SEEK event
                    seekTo(mediaEl.currentTime - 5.0);
                    break;
                case '.':
                    e.preventDefault();
                    if (onBeforeSeek && !onBeforeSeek())
                        break;
                    clearRewind();
                    seekTo(mediaEl.currentTime + 5.0);
                    break;
                case 'm':
                case 'M':
                    onMuteToggle?.();
                    break;
                case 'c':
                case 'C':
                    dispatch({ type: 'TOGGLE_CAPTION_PICKER' });
                    break;
                case 'Escape':
                    dispatch({ type: 'CLOSE_ALL_MENUS' });
                    break;
                case 't':
                case 'T':
                    dispatch({ type: 'TOGGLE_TIMECODE_POPOVER' });
                    break;
            }
        };
        const handleKeyUp = (e) => {
            const key = e.key.toLowerCase();
            if (key === 'k') {
                isKHeldRef.current = false;
                clearSlowStep();
                slowStepActiveRef.current = false;
            }
            if (key === 'j' || key === 'l') {
                clearSlowStep();
                slowStepActiveRef.current = false;
            }
        };
        // keydown stays on containerEl so we don't capture keys typed in other
        // parts of the page. keyup attaches to the document so we always catch
        // key release even if focus shifted away from containerEl between
        // press and release — without this, isKHeldRef can get stuck true and
        // make subsequent L/J presses incorrectly trigger slow-step.
        containerEl.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);
        return () => {
            containerEl.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
            clearRewind();
            clearSlowStep();
        };
    }, [
        containerEl,
        mediaEl,
        dispatch,
        togglePlayback,
        clearRewind,
        clearSlowStep,
        rewind,
        fastForward,
        incrementFrames,
        startSlowStepBack,
        startSlowStepForward,
        seekTo,
        onFullscreenToggle,
        onMuteToggle,
        onNextMarker,
        onPrevMarker,
        onBeforeSeek,
        basePlaybackRateRef,
        isFollower,
    ]);
}
